import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useToast } from "@/components/SimpleToast";
import { WEBINAR_SETS, WEBINAR_CATEGORIES, WEBINAR_STYLES } from "@/data/webinarBackgrounds";
import { Download, Maximize2, Presentation, X, FolderDown, Video, Info, Copy } from "lucide-react";

const HOW_TO = [
  { tool: "Zoom", steps: 'Einstellungen → Hintergrund & Effekte → „+" → Bild hinzufügen' },
  { tool: "Microsoft Teams", steps: 'Vor/während Meeting → Hintergrundeffekte → „Neu hinzufügen"' },
  { tool: "Google Meet", steps: "Vor Beitritt → Hintergrund-Symbol → Bild hochladen" },
];


const BASE = "/assets/webinar";
const V = "?v=4";
const API = process.env.REACT_APP_BACKEND_URL;

export default function WebinarBackgrounds() {
  const toast = useToast();
  const [preview, setPreview] = useState(null);
  const [zipping, setZipping] = useState(false);
  const [activeStyle, setActiveStyle] = useState("light");
  const [activeCat, setActiveCat] = useState("all");

  const activeSet = WEBINAR_SETS[activeStyle];
  const filtered =
    activeCat === "all" ? activeSet : activeSet.filter((b) => b.cat === activeCat);

  const copyHowTo = async (tool) => {
    const h = HOW_TO.find((x) => x.tool === tool);
    if (!h) return;
    const text = `${h.tool} – Virtuellen Hintergrund einrichten:\n${h.steps}`;
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`Anleitung für ${tool} kopiert`);
    } catch (e) {
      toast.error("Kopieren nicht möglich");
    }
  };

  const handleDownloadAll = async () => {
    setZipping(true);
    try {
      const res = await fetch(`${API}/api/webinar-backgrounds/download-all?style=${activeStyle}`, { credentials: "include" });
      if (!res.ok) throw new Error("zip failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "R-NetWork-Webinar-Hintergruende.zip";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Alle Hintergründe als ZIP heruntergeladen");
    } catch (e) {
      toast.error("ZIP-Download fehlgeschlagen");
    } finally {
      setZipping(false);
    }
  };

  const handleDownload = async (bg) => {
    try {
      const res = await fetch(`${BASE}/${bg.file}${V}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `R-NetWork-Webinar-${bg.title.replace(/[^\wäöüÄÖÜß-]+/g, "_")}.jpg`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`"${bg.title}" heruntergeladen`);
    } catch (e) {
      toast.error("Download fehlgeschlagen");
    }
  };

  return (
    <div className="space-y-6" data-testid="webinar-backgrounds-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex items-start gap-3">
          <div className="w-11 h-11 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
            <Presentation className="w-6 h-6 text-primary" strokeWidth={1.5} />
          </div>
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Webinar-Hintergründe</h1>
            <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
              Professionelle Hintergründe (16:9, 1920×1080) mit dem R-NetWork GmbH Logo in drei
              Stilen – hell &amp; freundlich (30 Motive), dunkel &amp; elegant (10 Motive) oder mit
              DocCirrus-Branding (30 Motive). Ideal als virtueller Hintergrund in Zoom, Teams oder
              Google Meet.
            </p>
          </div>
        </div>
        <Button
          onClick={handleDownloadAll}
          disabled={zipping}
          className="shrink-0 self-start sm:self-auto"
          data-testid="webinar-download-all"
        >
          <FolderDown className="w-4 h-4 mr-2" />
          {zipping ? "Wird gepackt …" : "Alle herunterladen (ZIP)"}
        </Button>
      </div>

      {/* Anleitung: als virtueller Hintergrund verwenden */}
      <div className="rounded-xl border border-border bg-muted/40 p-4 sm:p-5" data-testid="webinar-howto">
        <div className="flex items-center gap-2 mb-3">
          <Info className="w-4 h-4 text-primary" />
          <h2 className="text-base font-semibold">So nutzt du die Bilder als virtuellen Hintergrund</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {HOW_TO.map((h) => (
            <div
              key={h.tool}
              className="rounded-lg bg-background border border-border p-3"
              data-testid={`howto-${h.tool.split(" ")[0].toLowerCase()}`}
            >
              <div className="flex items-center gap-2 mb-1">
                <Video className="w-4 h-4 text-primary shrink-0" />
                <span className="font-semibold text-sm">{h.tool}</span>
              </div>
              <p className="text-sm text-muted-foreground leading-snug">{h.steps}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Stil-Umschalter */}
      <div className="flex items-center gap-2" data-testid="webinar-style-toggle">
        <span className="text-sm font-medium text-muted-foreground mr-1">Stil:</span>
        {WEBINAR_STYLES.map((s) => {
          const active = activeStyle === s.key;
          return (
            <button
              key={s.key}
              type="button"
              onClick={() => {
                setActiveStyle(s.key);
                setActiveCat("all");
              }}
              className={`px-4 py-1.5 rounded-full text-sm font-semibold border transition-colors ${
                active
                  ? "bg-primary text-primary-foreground border-primary"
                  : "bg-background text-foreground border-border hover:bg-muted"
              }`}
              data-testid={`webinar-style-${s.key}`}
            >
              {s.label} <span className={active ? "opacity-80" : "text-muted-foreground"}>({WEBINAR_SETS[s.key].length})</span>
            </button>
          );
        })}
      </div>

      {/* Kategorie-Filter */}
      <div className="flex flex-wrap gap-2" data-testid="webinar-category-filter">
        {WEBINAR_CATEGORIES.map((c) => {
          const count =
            c.key === "all"
              ? activeSet.length
              : activeSet.filter((b) => b.cat === c.key).length;
          if (count === 0 && c.key !== "all") return null;
          const active = activeCat === c.key;
          return (
            <button
              key={c.key}
              type="button"
              onClick={() => setActiveCat(c.key)}
              className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${
                active
                  ? "bg-primary text-primary-foreground border-primary"
                  : "bg-background text-foreground border-border hover:bg-muted"
              }`}
              data-testid={`webinar-cat-${c.key}`}
            >
              {c.label} <span className={active ? "opacity-80" : "text-muted-foreground"}>({count})</span>
            </button>
          );
        })}
      </div>

      {/* Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5">
        {filtered.map((bg, i) => (
          <div
            key={bg.file}
            className="group rounded-xl overflow-hidden border border-border bg-card shadow-sm hover:shadow-md transition-shadow"
            data-testid={`webinar-card-${i}`}
          >
            <button
              type="button"
              onClick={() => setPreview(bg)}
              className="relative block w-full aspect-video overflow-hidden"
              data-testid={`webinar-preview-${i}`}
            >
              <img
                src={`${BASE}/${bg.file}${V}`}
                alt={bg.title}
                loading="lazy"
                className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-[1.03]"
              />
              <span className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                <Maximize2 className="w-7 h-7 text-white opacity-0 group-hover:opacity-100 transition-opacity drop-shadow" />
              </span>
            </button>
            <div className="p-3 flex items-center justify-between gap-2">
              <div className="min-w-0">
                <p className="font-semibold text-sm truncate">{bg.title}</p>
                <p className="text-xs text-muted-foreground truncate">{bg.desc}</p>
              </div>
              <div className="flex items-center gap-1.5 shrink-0">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="px-2"
                      title="Anleitung kopieren"
                      data-testid={`webinar-howto-menu-${i}`}
                    >
                      <Video className="w-4 h-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuLabel>Anleitung kopieren</DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    {HOW_TO.map((h) => (
                      <DropdownMenuItem
                        key={h.tool}
                        onClick={() => copyHowTo(h.tool)}
                        data-testid={`webinar-howto-copy-${h.tool.split(" ")[0].toLowerCase()}-${i}`}
                      >
                        <Copy className="w-4 h-4 mr-2" />
                        In {h.tool} verwenden
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleDownload(bg)}
                  data-testid={`webinar-download-${i}`}
                >
                  <Download className="w-4 h-4 sm:mr-2" />
                  <span className="hidden sm:inline">Herunterladen</span>
                </Button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Vollbild-Vorschau */}
      <Dialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
        <DialogContent className="max-w-5xl p-0 overflow-hidden bg-black border-0">
          <DialogTitle className="sr-only">{preview?.title || "Hintergrund-Vorschau"}</DialogTitle>
          <DialogDescription className="sr-only">Vollbild-Vorschau des Webinar-Hintergrunds.</DialogDescription>
          {preview && (
            <div className="relative">
              <img src={`${BASE}/${preview.file}${V}`} alt={preview.title} className="w-full h-auto" />
              <div className="absolute bottom-0 left-0 right-0 flex items-center justify-between gap-2 p-4 bg-gradient-to-t from-black/70 to-transparent">
                <span className="text-white font-medium">{preview.title}</span>
                <Button size="sm" onClick={() => handleDownload(preview)} data-testid="webinar-preview-download">
                  <Download className="w-4 h-4 mr-2" />
                  Herunterladen
                </Button>
              </div>
              <button
                type="button"
                onClick={() => setPreview(null)}
                className="absolute top-3 right-3 w-9 h-9 rounded-full bg-black/50 hover:bg-black/70 text-white flex items-center justify-center"
                data-testid="webinar-preview-close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
